import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

import { OUTSTANDING_LAB_TESTS_QUERY_KEY } from "./useOutstandingLabTests";
import { PATIENT_OUTSTANDING_LAB_TESTS_QUERY_KEY } from "./usePatientOutstandingLabTests";

/**
 * Marks a waiting test as one that will not come back — the sample was rejected, the
 * test was cancelled, or it was sent elsewhere (KIN-2087). The reason is required by the
 * backend and recorded with who closed it and when; it cannot be undone through the API,
 * so the caller asks before sending.
 *
 * skipGlobalError because the one failure that matters here has its own answer: a 409
 * means the test was resulted or closed while the list sat on screen, which is not an
 * error to report but a list to refresh. Both outstanding lists are invalidated either
 * way — after a 409 the stale row is exactly what has to go.
 */
export const useCloseLabOrderTest = () => {
  const queryClient = useQueryClient();
  const tenantVal = localStorage.getItem("kinesin-tenant");

  const invalidateOutstanding = () => {
    queryClient.invalidateQueries({
      queryKey: [OUTSTANDING_LAB_TESTS_QUERY_KEY],
    });
    queryClient.invalidateQueries({
      queryKey: [PATIENT_OUTSTANDING_LAB_TESTS_QUERY_KEY],
    });
  };

  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async ({ testId, reason }) => {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}/healthlink/lab-orders/tests/${testId}/close`,
        { reason }
      );

      return response.data;
    },
    onSuccess: invalidateOutstanding,
    onError: invalidateOutstanding,
  });
};
